'use client';

import Link from 'next/link';
import { BookOpen, Search, ExternalLink, Shield, TrendingUp } from 'lucide-react';

interface CWERelatedResourcesProps {
  cweId: string;
  cweNumericId: number;
  isTop25?: boolean;
}

export default function CWERelatedResources({ cweId, cweNumericId, isTop25 }: CWERelatedResourcesProps) {
  return (
    <section className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mt-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        Learn More
      </h3>

      <div className="grid md:grid-cols-2 gap-4">
        {/* CVE Lookup - Find CVEs using this CWE */}
        <Link
          href={`/tools/security/cve-lookup?cwe=${cweNumericId}`}
          className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-colors group"
        >
          <Search className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              Find Related CVEs
            </span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Search for vulnerabilities that exploit {cweId}
            </p>
          </div>
        </Link>

        {/* CWE Top 25 */}
        {isTop25 && (
          <Link
            href="/tools/security/cwe-lookup/top25"
            className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-500 transition-colors group"
          >
            <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                CWE Top 25 Most Dangerous
              </span>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                See how this weakness ranks against others
              </p>
            </div>
          </Link>
        )}

        {/* Blog: CVE vs CWE */}
        <Link
          href="/blog/what-is-the-difference-between-cve-and-cwe"
          className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-green-400 dark:hover:border-green-500 transition-colors group"
        >
          <BookOpen className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
              CVE vs CWE: What&apos;s the Difference?
            </span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Understanding vulnerabilities vs weaknesses
            </p>
          </div>
        </Link>

        {/* Blog: CVSS Scoring */}
        <Link
          href="/blog/cvss-scoring-system-explained"
          className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-orange-400 dark:hover:border-orange-500 transition-colors group"
        >
          <Shield className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
              Understanding CVSS Scoring
            </span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              How vulnerability severity is measured
            </p>
          </div>
        </Link>

        {/* MITRE CWE Link */}
        <a
          href={`https://cwe.mitre.org/data/definitions/${cweNumericId}.html`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 transition-colors group"
        >
          <ExternalLink className="w-5 h-5 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium text-gray-900 dark:text-white group-hover:text-gray-600 dark:group-hover:text-gray-400 transition-colors">
              View Full MITRE Entry
            </span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Complete technical details and references
            </p>
          </div>
        </a>
      </div>
    </section>
  );
}
